// Shared replay skeleton: step index + listener + seek/stepBackward built on
// the subclass-supplied `reset()` and `applyStep(i)` hooks. Subclasses provide
// their own typed `stepForward()` so they can return a rich descriptor (e.g.
// backgammon's enriched ReplayEvent, checkers' { preState, postState, move }).
//
// Replay state is rebuilt from initial on every backward seek — fine because
// games are < 200 moves and apply functions are cheap.
export abstract class BaseReplayController {
  protected stepIndex = 0;
  private listener: () => void = () => {};

  // Total number of replayable steps. May differ from raw payload count
  // (backgammon: roll + N moves per turn).
  abstract getTotalSteps(): number;

  // Reset internal state to "before any step has been applied".
  protected abstract reset(): void;

  // Apply the step at `index`, advancing internal state by one step.
  protected abstract applyStep(index: number): void;

  getStepIndex(): number {
    return this.stepIndex;
  }

  isAtStart(): boolean {
    return this.stepIndex === 0;
  }

  isAtEnd(): boolean {
    return this.stepIndex >= this.getTotalSteps();
  }

  setListener(fn: () => void): void {
    this.listener = fn;
  }

  stepBackward(): void {
    if (this.stepIndex === 0) return;
    this.seek(this.stepIndex - 1);
  }

  seek(target: number): void {
    const clamped = Math.max(0, Math.min(this.getTotalSteps(), target));
    if (clamped === this.stepIndex) return;
    this.reset();
    for (let i = 0; i < clamped; i += 1) this.applyStep(i);
    this.stepIndex = clamped;
    this.notify();
  }

  // Subclass helper — call after applying a single step in their stepForward.
  protected advanceIndex(): void {
    this.stepIndex += 1;
    this.notify();
  }

  protected notify(): void {
    this.listener();
  }
}
